<template>
  <div class="item-spinner daily-spinner" :style="{height: `${itemSize + 32}px`}">
    <div class="overlay">
      <slot name="overlay" />
    </div>

    <v-img
      class="arrow" :src="require('@/assets/img/games/wheel/arrow.png')" contain
      width="64" aspect-ratio="1"
    />
    <div class="spinner" :style="spinnerStyles">
      <div v-if="spinnerItems.length" class="spinner-items">
        <v-responsive
          v-for="(spinnerItem, i) in spinnerItems"
          :key="i"
          :width="itemSize"
          :aspect-ratio="1"
          :class="{'is-won-item': spinnerItem.isWon}"
        >
          <CaseSpinnerItem
            :item="spinnerItem" class="grey600 fill-height"
          />
        </v-responsive>
      </div>
    </div>
  </div>
</template>

<script>
import anime from 'animejs/lib/anime.es'
import seededRandom from 'seed-random'

import CaseSpinnerItem from '@/components/CaseSpinnerItem'
import { registerSound } from '@/modules/sound'
import { getRandomIntInRange } from '@/utils'
import { pickItems } from '@/views/games/Cases/Case/utils'

const sounds = {
  glow: registerSound('games/coinflip/glow', { volume: 0.5 }),
  land: registerSound('games/cases/land', { volume: 0.3 }),
  bigWin: registerSound('games/cases/big_win', { volume: 0.7 }),
  start: registerSound('games/cases/spin', { volume: 0.3 }),
  dud: registerSound('games/cases/dud', { volume: 0.25 }),
  ticks: [
    registerSound('games/wheel/tick1', { volume: 0.15 }),
    registerSound('games/wheel/tick2', { volume: 0.15 }),
    registerSound('games/wheel/tick3', { volume: 0.15 }),
    registerSound('games/wheel/tick4', { volume: 0.15 }),
  ],
}

export default {
  components: {
    CaseSpinnerItem,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    rollDuration: {
      type: Number,
      default: 8e3,
    },
    itemCount: {
      type: Number,
      default: 80,
    },
    itemCountBuffer: {
      type: Number,
      default: 10,
    },
    itemSize: {
      type: Number,
      default: 120,
    },
    itemGap: {
      type: Number,
      default: 8,
    },
  },
  data: () => ({
    animation: null,
    spinning: false,
    item: null,
    seed: null,
    spinnerItems: [],
  }),
  computed: {
    spinnerStyles() {
      return {
        height: `${this.itemSize}px`,
        '--itemSize': `${this.itemSize}px`,
        '--itemGap': `${this.itemGap}px`,
      }
    },
    hasWeight() {
      return this.items.some(i => i.weight)
    },
  },
  beforeDestroy() {
    this.killAnim()
  },
  methods: {
    async spin(item, seed) {
      if (this.spinning) {
        this.killAnim()
      }

      this.spinning = true

      this.item = item
      this.seed = seed

      this.spinnerItems = this.generateSpinnerItems(this.items, item, seed)

      // wait for render
      await this.$nextTick()

      //
      const {
        itemSize, itemGap, itemCount, rollDuration,
      } = this

      const totalGap = itemCount * itemGap
      const totalSize = itemCount * itemSize
      const halfItemSize = itemSize / 2

      const rand = seededRandom(seed)()
      const monkaSFactor = rand * (-halfItemSize - halfItemSize) + halfItemSize

      const rollOffset = totalSize + totalGap + halfItemSize + monkaSFactor
      const easingModifier = +(rand * (0.70 - 0.95) + 0.95).toFixed(2)

      let lastOffset = 0

      const $spinner = this.$el

      this.animation = anime
        .timeline()
        .add({
          targets: $spinner.querySelector('.overlay'),
          translateY: [0, '100%'],
          duration: 800,
          delay: 400,
          easing: 'easeOutBounce',
          begin(anim) {
            sounds.start.play()
          },
        })
        .add({
          targets: $spinner.querySelector('.spinner-items'),
          translateX: [0, -rollOffset],
          duration: rollDuration,
          easing: `cubicBezier(0.5, 1, ${easingModifier}, 1)`,
          update(anim) {
            const val = parseFloat(anim.animations?.[0]?.currentValue ?? 0)
            const offset = Math.abs(val)

            if (offset > lastOffset + itemSize) {
              sounds.ticks[getRandomIntInRange(0, 3)].play()
              lastOffset = offset
            }
          },
        }, '-=900')
        .add({
          targets: $spinner.querySelector('.item-spinner .spinner-items'),
          translateX: -(totalSize + totalGap + halfItemSize),
          duration: 500,
          easing: 'easeInOutCirc',
          begin(anim) {
            sounds.land.play()
          },
        }, '+=300')
        .add({
          targets: $spinner.querySelectorAll('.is-won-item .spinner-item'),
          scale: [1, 1.1],
          ...(item.price > 100e2 && { rotateY: [0, '360deg'] }),
          duration: 500,
          easing: 'easeOutQuart',
          begin(anim) {
            if (!item.price) {
              sounds.dud.play()
              return
            }

            sounds.glow.play()

            if (item.price > 100e2) {
              sounds.bigWin.play()
            }
          },
        })
        .add({
          targets: $spinner.querySelectorAll('.spinner-items > :not(.is-won-item)'),
          scale: [1, 0.95],
          opacity: [1, 0.3],
          duration: 700,
          easing: 'easeOutQuart',
        }, '-=500')

      await this.animation.finished

      this.spinning = false

      return item
    },
    async reset() {
      this.animation.seek(0)
    },
    killAnim() {
      if (!this.animation) return

      this.animation.pause()
      this.animation.animatables.forEach(({ target }) => {
        anime.remove(target)
      })
    },
    generateSpinnerItems(items, item, seed) {
      if (!items.length) return []
      if (!seed) return []

      const weightRemappedItems = items.map(i => ({
        ...i,
        weight: Math.ceil((i.weight ** 0.4) * 100),
      }))

      const { itemCount, itemCountBuffer, hasWeight } = this
      const spinnerItems = Array(itemCount + itemCountBuffer)

      for (let i = 0; i < spinnerItems.length; i++) {
        if (i === itemCount && item) {
          spinnerItems[i] = { ...item, isWon: true }
        } else {
          const rand = seededRandom(`${seed}-${i}`)()
          let randItem
          if (hasWeight) {
            randItem = pickItems(weightRemappedItems, rand)
          } else {
            randItem = items[Math.floor(rand * items.length)]
          }

          spinnerItems[i] = randItem
        }
      }

      return spinnerItems
    },
  },
}
</script>

<style lang="scss" scoped>
.item-spinner {
  overflow: hidden;
  position: relative;
}

.overlay {
  @include overlay;

  z-index: 3;
  background: url('../assets/img/games/cases/spinner-overlay.jpg') center center no-repeat;
  background-size: cover;
}

.arrow {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 50%;
  transform: translate(-50%, 0%);
  pointer-events: none;
}

.spinner {
  @include overlay;

  overflow: hidden;
  top: 50%;
  transform: translateY(-50%);

  .spinner-items {
    display: grid;
    position: absolute;
    left: 50%;
    grid-auto-flow: column;
    grid-template-columns: repeat(auto-fill, var(--itemSize));
    grid-gap: var(--itemGap);
    will-change: transform;
  }
}

</style>
